import {
  Button,
  Field,
  fieldLabelId,
  Input,
  Stack,
  Text,
} from '@components/ui';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import MenuItem from '@mui/material/MenuItem';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

const cancelJobReasons = [
  'Inaccurate information',
  'Duplicate rating',
  'Location or Commute',
  'Technical issues',
  'Other',
] as const;

type CancelJobReason = (typeof cancelJobReasons)[number];

const reasonLabelKey: Record<CancelJobReason, string> = {
  'Inaccurate information': 'cancelJob.reasons.inaccurate',
  'Duplicate rating': 'cancelJob.reasons.duplicate',
  'Location or Commute': 'cancelJob.reasons.location',
  'Technical issues': 'cancelJob.reasons.technical',
  Other: 'cancelJob.reasons.other',
};

const isCancelJobReason = (value: string): value is CancelJobReason =>
  (cancelJobReasons as readonly string[]).includes(value);

interface CancelJobDialogProps {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

export const CancelJobDialog = ({
  open,
  loading = false,
  onClose,
  onConfirm,
}: CancelJobDialogProps): JSX.Element => {
  const { t } = useTranslation();
  const [reason, setReason] = useState<CancelJobReason | ''>('');
  const [otherReason, setOtherReason] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const isOther = reason === 'Other';
  const trimmedOther = otherReason.trim();
  const reasonError =
    submitted && !reason ? t('cancelJob.reasonRequired') : undefined;
  const otherError =
    submitted && isOther && !trimmedOther
      ? t('cancelJob.otherRequired')
      : undefined;

  const reset = (): void => {
    setReason('');
    setOtherReason('');
    setSubmitted(false);
  };

  const handleClose = (): void => {
    if (!loading) onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setSubmitted(true);
    if (!reason || (isOther && !trimmedOther)) return;
    onConfirm(isOther ? trimmedOther : reason);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="cancel-job-title"
      aria-describedby="cancel-job-description"
      TransitionProps={{ onExited: reset }}
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          border: 1,
          borderColor: 'divider',
          borderRadius: 3,
          width: '100%',
          maxWidth: 440,
          m: 2,
          p: 3,
        },
      }}
    >
      <Box component="form" noValidate onSubmit={handleSubmit}>
        <Stack gap="sm" sx={{ mb: 2.5 }}>
          <Text
            id="cancel-job-title"
            variant="subtitle"
            sx={{ fontWeight: 700 }}
          >
            {t('cancelJob.title')}
          </Text>
          <Text
            id="cancel-job-description"
            variant="body"
            tone="muted"
            sx={{ fontSize: 14 }}
          >
            {t('cancelJob.body')}
          </Text>
        </Stack>

        <Field
          label={t('cancelJob.reasonLabel')}
          htmlFor="cancel-job-reason"
          error={reasonError}
        >
          <Input
            select
            id="cancel-job-reason"
            value={reason}
            invalid={Boolean(reasonError)}
            disabled={loading}
            onChange={(event) => {
              const { value } = event.target;
              setReason(isCancelJobReason(value) ? value : '');
            }}
            SelectProps={{
              displayEmpty: true,
              labelId: fieldLabelId('cancel-job-reason'),
              renderValue: (value) =>
                typeof value === 'string' && isCancelJobReason(value) ? (
                  t(reasonLabelKey[value])
                ) : (
                  <Box component="span" sx={{ color: 'text.disabled' }}>
                    {t('cancelJob.reasonPlaceholder')}
                  </Box>
                ),
            }}
          >
            {cancelJobReasons.map((option) => (
              <MenuItem key={option} value={option}>
                {t(reasonLabelKey[option])}
              </MenuItem>
            ))}
          </Input>
        </Field>

        {isOther ? (
          <Field
            label={t('cancelJob.otherLabel')}
            htmlFor="cancel-job-other"
            error={otherError}
            sx={{ mt: 1 }}
          >
            <Input
              id="cancel-job-other"
              autoFocus
              value={otherReason}
              invalid={Boolean(otherError)}
              disabled={loading}
              placeholder={t('cancelJob.otherPlaceholder')}
              inputProps={{ maxLength: 500 }}
              onChange={(event) => setOtherReason(event.target.value)}
            />
          </Field>
        ) : null}

        <Stack direction="row" gap="md" sx={{ mt: 2 }}>
          <Button
            variant="secondary"
            sx={{ flex: 1 }}
            onClick={handleClose}
            disabled={loading}
          >
            {t('cancelJob.keep')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            sx={{ flex: 1 }}
            loading={loading}
          >
            {t('cancelJob.confirm')}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};
